import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Avatar,
} from '@mui/material';
import {
  ContactMail as ContactIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Note as NoteIcon,
} from '@mui/icons-material';
import { useDataProvider } from 'react-admin';
import { formatDistanceToNow } from 'date-fns';
import { ContactLogDialog } from './ContactLogDialog';

interface ContactLog {
  id?: string;
  type: 'Call' | 'Email' | 'SMS' | 'InPerson' | 'Other';
  direction: 'Inbound' | 'Outbound';
  subject: string;
  notes?: string;
  contactedById?: number;
  contactDate: string;
}

interface ContactLogListProps {
  customerId?: string;
  leadId?: string;
}

export const ContactLogList: React.FC<ContactLogListProps> = ({ customerId, leadId }) => {
  const dataProvider = useDataProvider();
  const [contactLogs, setContactLogs] = useState<ContactLog[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState<ContactLog | null>(null);
  const [users, setUsers] = useState<Array<{ id: number; name: string; email: string }>>([]);

  const loadContactLogs = useCallback(async () => {
    try {
      let filter = {};
      if (customerId) {
        filter = { customerId };
      } else if (leadId) {
        filter = { leadId };
      }

      const response = await dataProvider.getList('contact-logs', {
        filter,
        sort: { field: 'contactDate', order: 'DESC' },
        pagination: { page: 1, perPage: 50 },
      });

      if (response) {
        setContactLogs(response.data);
      }
    } catch (error) {
      // Error loading contact logs
    }
  }, [customerId, leadId, dataProvider]);

  const loadUsers = useCallback(async () => {
    try {
      const response = await dataProvider.getList('users', {
        filter: {},
        pagination: { page: 1, perPage: 100 },
        sort: { field: 'name', order: 'ASC' },
      });
      setUsers(response.data);
    } catch (error) {
      // Error loading users
    }
  }, [dataProvider]);

  useEffect(() => {
    loadContactLogs();
    loadUsers();
  }, [loadContactLogs, loadUsers]);

  const handleAddNew = () => {
    setEditingLog(null);
    setDialogOpen(true);
  };

  const handleEdit = (log: ContactLog) => {
    setEditingLog(log);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingLog(null);
    loadContactLogs(); // Refresh the list
  };

  const getContactTypeIcon = (type: string) => {
    switch (type) {
      case 'Call':
        return <PhoneIcon />;
      case 'Email':
        return <EmailIcon />;
      case 'SMS':
        return <PhoneIcon />;
      case 'InPerson':
        return <PersonIcon />;
      case 'Other':
        return <NoteIcon />;
      default:
        return <ContactIcon />;
    }
  };

  const getContactTypeColor = (type: string) => {
    switch (type) {
      case 'Call':
        return 'success';
      case 'Email':
        return 'info';
      case 'SMS':
        return 'secondary';
      case 'InPerson':
        return 'warning';
      case 'Other':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <>
      <Card>
        <CardContent>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6" color="primary">
              Contact Log
            </Typography>
            <Button startIcon={<AddIcon />} variant="contained" onClick={handleAddNew}>
              Add Contact Log
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {contactLogs.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
              <ContactIcon sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="body1">
                No contact logs found. Click &quot;Add Contact Log&quot; to create one.
              </Typography>
            </Box>
          ) : (
            <List>
              {contactLogs.map((log, index) => (
                <React.Fragment key={log.id || index}>
                  <ListItem
                    alignItems="flex-start"
                    button
                    onClick={() => handleEdit(log)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Avatar
                      sx={{ mr: 2, mt: 0.5, bgcolor: `${getContactTypeColor(log.type)}.main` }}
                    >
                      {getContactTypeIcon(log.type)}
                    </Avatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {log.subject}
                          </Typography>
                          <Chip
                            label={log.type.toUpperCase()}
                            size="small"
                            color={getContactTypeColor(log.type) as any}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {log.notes}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.direction} •{' '}
                            {formatDistanceToNow(new Date(log.contactDate), { addSuffix: true })}
                            {log.contactedById &&
                              ` • By: ${
                                users.find(u => u.id === log.contactedById)?.name || 'User'
                              }`}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < contactLogs.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <ContactLogDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        customerId={customerId}
        leadId={leadId}
        editingLog={editingLog}
      />
    </>
  );
};
